import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import { noop } from "./functions";
import { type ApiError } from "../types";
import { API_URL } from "../constants";

export const initErrorMonitoring = (
  dsn?: string,
  environment?: string
): void => {
  if (!dsn || !environment) {
    return;
  }

  // disable console log in production
  if (environment === "production") {
    // eslint-disable-next-line no-console
    console.log = noop;
  }

  Sentry.init({
    dsn,
    sendClientReports: false,
    tunnel: `${API_URL}/sentry_tunnel/`,
    environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/.*\.archway\.finance\/api/,
        ],
        networkDetailDenyUrls: [
          /^https:\/\/.*\.archway\.finance\/api\/auth/,
          /^https:\/\/.*\.archway\.finance\/api\/accounts\/reset/,
          /^https:\/\/.*\.archway\.finance\/api\/accounts\/set/,
        ],
      }),
      // Note: Sentry.feedbackIntegration is in App() App.txs
    ],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.archway\.finance\/api/,
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // replays
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
};

export const captureError = (error: Error | ApiError): string => {
  const apiError = error as ApiError;

  if (
    apiError &&
    typeof apiError.error === "string" &&
    typeof apiError.status === "string"
  ) {
    return Sentry.captureException(`${apiError.status}: ${apiError.error}`);
  }

  return Sentry.captureException(error);
};
